import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { marcasAutos, marcasCamiones, categoriasCamiones, autosDestacadosMock, camionesDestacadosMock } from '../../mocks/autos';

type TabType = 'autos' | 'camiones';

const AutosPage = () => {
  const [tab, setTab] = useState<TabType>('autos');
  const [filtroMarca, setFiltroMarca] = useState('');
  const [filtroModelo, setFiltroModelo] = useState('');
  const [filtroCondicion, setFiltroCondicion] = useState('');
  const [filtroPrecioMax, setFiltroPrecioMax] = useState('');
  const [filtroCategoriaCamion, setFiltroCategoriaCamion] = useState('');
  const [filtroMarcaCamion, setFiltroMarcaCamion] = useState('');
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedVehiculo, setSelectedVehiculo] = useState<any>(null);
  const [contactForm, setContactForm] = useState({ nombre: '', telefono: '', mensaje: '' });

  useEffect(() => {
    document.title = 'Autos y Vehículos en Puerto Rico | IslaVende - Clasificados de Autos PR';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Compra y vende autos, SUVs y camiones en Puerto Rico. El mejor sitio de clasificados de autos en PR. Publicación GRATIS, contacto directo con el vendedor. Miles de vehículos disponibles en toda la isla.');
  }, []);

  const modelosDisponibles = filtroMarca ? marcasAutos[filtroMarca] || [] : [];
  const modelosCamionDisponibles = filtroMarcaCamion ? marcasCamiones[filtroMarcaCamion] || [] : [];

  const autosFiltrados = autosDestacadosMock.filter(auto => {
    if (filtroMarca && auto.marca !== filtroMarca) return false;
    if (filtroModelo && auto.modelo !== filtroModelo) return false;
    if (filtroCondicion && auto.condicion !== filtroCondicion) return false;
    if (filtroPrecioMax && auto.precio > Number(filtroPrecioMax)) return false;
    return true;
  });

  const camionesFiltrados = camionesDestacadosMock.filter(c => {
    if (filtroMarcaCamion && c.marca !== filtroMarcaCamion) return false;
    if (filtroCategoriaCamion && c.categoria_camion !== filtroCategoriaCamion) return false;
    if (filtroCondicion && c.condicion !== filtroCondicion) return false;
    if (filtroPrecioMax && c.precio > Number(filtroPrecioMax)) return false;
    return true;
  });

  const handleSolicitarInfo = (vehiculo: any) => {
    setSelectedVehiculo(vehiculo);
    setShowContactModal(true);
  };

  const limpiarFiltros = () => {
    setFiltroMarca('');
    setFiltroModelo('');
    setFiltroCondicion('');
    setFiltroPrecioMax('');
    setFiltroCategoriaCamion('');
    setFiltroMarcaCamion('');
  };

  const vehiculosActivos = tab === 'autos' ? autosFiltrados : camionesFiltrados;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3">
              Vitrina de Vehículos
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Autos, SUVs, camiones y vehículos pesados de los mejores dealers de Puerto Rico.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-2 mb-8 bg-white rounded-xl p-1 border border-gray-200 w-fit mx-auto">
            <button
              onClick={() => setTab('autos')}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                tab === 'autos'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-car-line mr-2"></i>
              Autos y SUVs
            </button>
            <button
              onClick={() => setTab('camiones')}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                tab === 'camiones'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-truck-line mr-2"></i>
              Camiones y Pesados
            </button>
          </div>

          {/* Filtros */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
            {tab === 'autos' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Marca</label>
                  <select
                    value={filtroMarca}
                    onChange={(e) => { setFiltroMarca(e.target.value); setFiltroModelo(''); }}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 cursor-pointer"
                  >
                    <option value="">Todas las marcas</option>
                    {Object.keys(marcasAutos).map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Modelo</label>
                  <select
                    value={filtroModelo}
                    onChange={(e) => setFiltroModelo(e.target.value)}
                    disabled={!filtroMarca}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">Todos los modelos</option>
                    {modelosDisponibles.map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Condición</label>
                  <select
                    value={filtroCondicion}
                    onChange={(e) => setFiltroCondicion(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 cursor-pointer"
                  >
                    <option value="">Todos</option>
                    <option value="Nuevo">Nuevo</option>
                    <option value="Usado">Usado</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Precio Máximo</label>
                  <input
                    type="number"
                    placeholder="Ej: 35000"
                    value={filtroPrecioMax}
                    onChange={(e) => setFiltroPrecioMax(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    onClick={limpiarFiltros}
                    className="w-full py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-refresh-line mr-1"></i>
                    Limpiar
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Marca</label>
                  <select
                    value={filtroMarcaCamion}
                    onChange={(e) => setFiltroMarcaCamion(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 cursor-pointer"
                  >
                    <option value="">Todas las marcas</option>
                    {Object.keys(marcasCamiones).map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Categoría</label>
                  <select
                    value={filtroCategoriaCamion}
                    onChange={(e) => setFiltroCategoriaCamion(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 cursor-pointer"
                  >
                    <option value="">Todas</option>
                    {categoriasCamiones.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Condición</label>
                  <select
                    value={filtroCondicion}
                    onChange={(e) => setFiltroCondicion(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 cursor-pointer"
                  >
                    <option value="">Todos</option>
                    <option value="Nuevo">Nuevo</option>
                    <option value="Usado">Usado</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Precio Máximo</label>
                  <input
                    type="number"
                    placeholder="Ej: 80000"
                    value={filtroPrecioMax}
                    onChange={(e) => setFiltroPrecioMax(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    onClick={limpiarFiltros}
                    className="w-full py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-refresh-line mr-1"></i>
                    Limpiar
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Contador */}
          <div className="mb-6">
            <p className="text-gray-600 text-sm">
              <strong>{vehiculosActivos.length}</strong> {vehiculosActivos.length === 1 ? 'vehículo encontrado' : 'vehículos encontrados'}
            </p>
          </div>

          {/* Grid de vehículos */}
          {vehiculosActivos.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center border border-gray-200">
              <i className="ri-car-line text-5xl text-gray-300 mb-4"></i>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No se encontraron vehículos</h3>
              <p className="text-gray-500 text-sm">Intenta ajustar los filtros para ver más opciones</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {vehiculosActivos.map((vehiculo: any) => (
                <VehiculoCard
                  key={vehiculo.id}
                  vehiculo={vehiculo}
                  esCamion={tab === 'camiones'}
                  onSolicitarInfo={handleSolicitarInfo}
                />
              ))}
            </div>
          )}

          {/* CTA Dealer */}
          <div className="mt-16 bg-gray-900 rounded-2xl p-8 sm:p-12 text-white text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-white/10 rounded-2xl mx-auto mb-4">
              <i className="ri-car-line text-4xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold mb-3">¿Eres un Dealer o Vendedor?</h2>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto text-base">
              Únete a IslaVende y muestra tu inventario a miles de compradores potenciales en toda la isla.
            </p>
            <Link
              to="/registro"
              className="inline-block px-8 py-4 bg-white text-gray-900 font-bold rounded-xl hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Registrar mi Negocio
            </Link>
          </div>
        </div>
      </div>

      {/* Modal de Contacto */}
      {showContactModal && selectedVehiculo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Solicitar Información</h3>
                <p className="text-gray-500 text-sm">
                  {selectedVehiculo.marca} {selectedVehiculo.modelo} {selectedVehiculo.anio}
                </p>
              </div>
              <button
                onClick={() => setShowContactModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nombre Completo</label>
                <input
                  type="text"
                  value={contactForm.nombre}
                  onChange={(e) => setContactForm({ ...contactForm, nombre: e.target.value })}
                  placeholder="Tu nombre"
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Teléfono</label>
                <input
                  type="tel"
                  value={contactForm.telefono}
                  onChange={(e) => setContactForm({ ...contactForm, telefono: e.target.value })}
                  placeholder="(787) 000-0000"
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Mensaje</label>
                <textarea
                  rows={3}
                  value={contactForm.mensaje}
                  onChange={(e) => setContactForm({ ...contactForm, mensaje: e.target.value })}
                  placeholder={`Hola, estoy interesado en el ${selectedVehiculo.marca} ${selectedVehiculo.modelo}...`}
                  maxLength={500}
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 resize-none"
                />
              </div>
              <button
                onClick={() => setShowContactModal(false)}
                className="w-full py-3 bg-gray-900 text-white font-semibold rounded-xl hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-send-plane-line mr-2"></i>
                Enviar Solicitud
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

interface VehiculoCardProps {
  vehiculo: any;
  esCamion: boolean;
  onSolicitarInfo: (v: any) => void;
}

const VehiculoCard = ({ vehiculo, esCamion, onSolicitarInfo }: VehiculoCardProps) => (
  <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-gray-400 transition-all hover:-translate-y-0.5">
    <div className="relative w-full h-64">
      <img
        src={vehiculo.imagen}
        alt={`${vehiculo.marca} ${vehiculo.modelo}`}
        className="w-full h-full object-cover object-top"
      />
      <span className={`absolute top-4 right-4 px-3 py-1.5 rounded-full text-xs font-bold ${
        vehiculo.condicion === 'Nuevo' ? 'bg-green-500 text-white' : 'bg-white text-gray-800'
      }`}>
        {vehiculo.condicion}
      </span>
      {esCamion && vehiculo.categoria_camion && (
        <span className="absolute top-4 left-4 bg-orange-500 text-white px-3 py-1.5 rounded-full text-xs font-bold">
          {vehiculo.categoria_camion}
        </span>
      )}
    </div>

    <div className="p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-0.5">
            {vehiculo.marca} {vehiculo.modelo}
          </h3>
          <p className="text-gray-500 text-sm">{vehiculo.dealer || vehiculo.vendedor}</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-gray-900">
            ${vehiculo.precio.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Specs */}
      <div className="grid grid-cols-3 gap-3 mb-4 pb-4 border-b border-gray-100">
        <div className="text-center">
          <i className="ri-calendar-line text-xl text-gray-400 mb-0.5"></i>
          <p className="text-xs text-gray-500">Año</p>
          <p className="font-bold text-gray-900 text-sm">{vehiculo.anio}</p>
        </div>
        <div className="text-center">
          <i className="ri-speed-line text-xl text-gray-400 mb-0.5"></i>
          <p className="text-xs text-gray-500">Millaje</p>
          <p className="font-bold text-gray-900 text-sm">{vehiculo.millaje}</p>
        </div>
        <div className="text-center">
          <i className="ri-map-pin-line text-xl text-gray-400 mb-0.5"></i>
          <p className="text-xs text-gray-500">Ubicación</p>
          <p className="font-bold text-gray-900 text-sm">{vehiculo.ubicacion}</p>
        </div>
      </div>

      {/* Badges especiales */}
      <div className="flex flex-wrap gap-2 mb-4">
        {vehiculo.titulo_en_mano && (
          <span className="flex items-center gap-1 px-2.5 py-1 bg-green-50 text-green-700 rounded-full text-xs font-semibold">
            <i className="ri-file-text-line"></i>
            Título en Mano
          </span>
        )}
        {vehiculo.garantia_dealer && (
          <span className="flex items-center gap-1 px-2.5 py-1 bg-amber-50 text-amber-700 rounded-full text-xs font-semibold">
            <i className="ri-shield-check-line"></i>
            Garantía de Dealer
          </span>
        )}
      </div>

      <button
        onClick={() => onSolicitarInfo(vehiculo)}
        className="w-full py-3 bg-gray-900 text-white font-semibold rounded-xl hover:bg-gray-800 transition-colors text-sm whitespace-nowrap cursor-pointer"
      >
        <i className="ri-mail-send-line mr-2"></i>
        Solicitar Información
      </button>
    </div>
  </div>
);

export default AutosPage;
